// @flow
import React from 'react'
import styles from './styles'
import formatters from 'lib/formatters'
import { setModal, unsetModal } from 'lib/modal/actions'
import { Table, Row, Cell } from '../Table'
import { Notes } from 'components/Notes'
import { S3FilePreview } from 'components/S3FilePreview'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from 'react-router-dom'
import { DateOfDeathRef, AnnualFeeRef, ClosingFeeRef } from 'components/References'
import { Loader } from 'components/Notifications'
import dateUtil from 'lib/dateUtil'
import { Dialog } from 'components/Dialog'
import { httpPost } from 'lib/http'
import { setLoad, setUnload } from 'lib/notification/actions'
import { setBanner } from 'lib/notification/actions'
import { isRetainFundsEnabled } from './utils'

type Props = {
  dispatch: Function,
  handleActionDelete: Function,
  handleFileDelete: Function,
  number: String,
  dateOfDeath: Date,
  events: [Object],
  notes: [Object],
  nextActions: [Object],
  openClosingDateModal: Function,
  openClosingEventModalCreate: Function,
  openClosingEventModalUpdate: Function,
  openClosingFeeModal: Function,
  openDateOfDeathModal: Function,
  onSuccess: Function,
  daysSinceLastAnnualFee: number,
  isLoading?: boolean,
  closing_fee_data: ?{
    date: string,
    amount: number,
  },
  was_final_tax_fee_taken: boolean,
  is_individual_account: boolean,
}

export const Template = (props: Props) => {
  const {
    dispatch,
    handleActionDelete,
    number,
    dateOfDeath,
    events,
    notes,
    nextActions,
    openClosingDateModal,
    openClosingEventModalCreate,
    openClosingEventModalUpdate,
    openClosingFeeModal,
    openDateOfDeathModal,
    daysSinceLastAnnualFee,
    isLoading = false,
    closing_fee_data,
    was_final_tax_fee_taken,
    is_individual_account,
    onSuccess,
  } = props

  const [showRetainFundsDialog, setShowRetainFundsDialog] = React.useState(false)
  const [isSubmittingRetainFunds, setIsSubmittingRetainFunds] = React.useState(false)
  const [showFinalTaxFeeDialog, setShowFinalTaxFeeDialog] = React.useState(false)
  const [isSubmittingFinalTaxFee, setIsSubmittingFinalTaxFee] = React.useState(false)

  if (isLoading) {
    return <Loader non_redux_loading={true} />
  }

  const handleRetainFunds = () => {
    setIsSubmittingRetainFunds(true)
    dispatch(setLoad())

    httpPost(`/admin/accounts/${number}/retain-funds`, {})
      .then(() => {
        dispatch(setBanner('Funds successfully retained for closing'))
        setShowRetainFundsDialog(false)
        onSuccess(number)
      })
      .catch(() => {
        dispatch(setBanner('Error retaining funds', 'danger'))
      })
      .then(() => {
        setIsSubmittingRetainFunds(false)
        dispatch(setUnload())
      })
  }

  const handleFinalTaxFee = () => {
    setIsSubmittingFinalTaxFee(true)
    dispatch(setLoad())

    httpPost(`/admin/accounts/${number}/final-tax-fee`, {})
      .then(() => {
        dispatch(setBanner('Final tax fee successfully applied'))
        setShowFinalTaxFeeDialog(false)
        window.location.reload()
      })
      .catch((err) => {
        const errors = err?.error?.errors || err?.errors
        const errorMessage = errors && errors.length > 0 ? errors[0].message : 'Error applying final tax fee'
        dispatch(setBanner(errorMessage, 'danger'))
        setShowFinalTaxFeeDialog(false)
      })
      .then(() => {
        setIsSubmittingFinalTaxFee(false)
        dispatch(setUnload())
      })
  }

  const deleteActionModal = (id) => {
    return (
      <section>
        <div className="section">
          <h4>Are you sure you want to delete this action?</h4>
        </div>
        <div className={styles.buttons}>
          <button className={styles.removeBtn} onClick={() => handleActionDelete(number, id)}>
            Delete
          </button>
          <span className={styles.spacer} />
          <button className={styles.cancelBtn} onClick={() => dispatch(unsetModal())}>
            Cancel
          </button>
        </div>
      </section>
    )
  }

  const renderEventRow = (e, idx) => {
    let notes = ' '
    const max = 50

    if (e.notes) {
      if (e.notes.length > max) {
        notes = e.notes.substring(0, max) + '...'
      } else {
        notes = e.notes
      }
    }

    const editLink = (
      <Link className={styles.iconLink} onClick={() => openClosingEventModalUpdate(e)}>
        <FontAwesomeIcon className={'fa-lg'} icon={'edit'} />
      </Link>
    )

    const deleteHandler = () => {
      dispatch(setModal(deleteActionModal(e.id)))
    }

    const dateDue = () => {
      const text = formatters.date(e.date_due)

      if (dateUtil.isDateBeforeToday(e.date_due) && !e.date_completed) {
        return <span style={{ color: 'red' }}>{text}</span>
      } else {
        return text
      }
    }

    const deleteLink = (
      <Link className={styles.iconLink} onClick={deleteHandler}>
        <FontAwesomeIcon className={'fa-lg'} icon={'times-circle'} />
      </Link>
    )

    return (
      <Row key={`events${idx}`}>
        <Cell value={formatters.date(e.date)} />
        <Cell value={e.event} />
        <Cell value={dateDue()} />
        <Cell value={formatters.date(e.date_completed)} />
        <Cell value={notes} italic={true} />
        <Cell value={e.user} />
        <Cell value={editLink} />
        <Cell value={deleteLink} />
      </Row>
    )
  }

  const renderEventsTable = () => {
    let rows = events.map(renderEventRow)

    return (
      <div className={styles.eventsTable}>
        <Table title={'Actions Taken'} headers={['Date', 'Event', 'Due', 'Completed', 'Note', 'User', ' ', ' ']} tableSize="large" tableType="read">
          {rows}
        </Table>
      </div>
    )
  }

  const renderNextActionRow = (event, idx) => {
    const link = (
      <Link className={styles.iconLink} onClick={() => openClosingEventModalCreate(event)}>
        <FontAwesomeIcon className={'fa-lg'} icon={'plus-circle'} />
      </Link>
    )

    return (
      <Row key={`row${idx}`}>
        <Cell className={styles.smallFixedCell} value={link} />
        <Cell value={event} />
      </Row>
    )
  }

  const renderNextActionsTable = (category, events) => {
    let rows = events.map(renderNextActionRow)

    return (
      <div className={styles.nextActionsTable} key={category}>
        <Table title={category} headers={[]} tableSize="large" tableType="read">
          {rows}
        </Table>
      </div>
    )
  }

  const renderNextActions = () => {
    let tables = []

    for (const dict of nextActions) {
      const table = renderNextActionsTable(dict.category, dict.actions)
      tables.push(table)
    }

    return <div>{tables}</div>
  }

  const setGoToAccountButton = (
    <Link className={styles.accountButton} to={`/accounts/${number}`}>
      Account Page
    </Link>
  )

  const setClosingFeeButton = (
    <Link
      className={styles.closingFeeButton}
      onClick={closing_fee_data ? undefined : () => openClosingFeeModal()}
      style={closing_fee_data ? { opacity: 0.5, pointerEvents: 'none', cursor: 'not-allowed' } : {}}
    >
      Apply Closing Fee
    </Link>
  )

  const isFinalTaxFeeEnabled = is_individual_account && !was_final_tax_fee_taken && closing_fee_data

  const finalTaxFeeButton = (
    <Link
      className={styles.closingFeeButton}
      onClick={isFinalTaxFeeEnabled ? () => setShowFinalTaxFeeDialog(true) : undefined}
      style={!isFinalTaxFeeEnabled ? { opacity: 0.5, pointerEvents: 'none', cursor: 'not-allowed' } : {}}
    >
      Final Tax Fee
    </Link>
  )

  const retainFundsButton = (
    <Link
      className={styles.closingFeeButton}
      onClick={isRetainFundsEnabled(number, closing_fee_data) ? () => setShowRetainFundsDialog(true) : undefined}
      style={!isRetainFundsEnabled(number, closing_fee_data) ? { opacity: 0.5, pointerEvents: 'none', cursor: 'not-allowed' } : {}}
    >
      Retain Funds
    </Link>
  )

  const setClosingDateButton = (
    <Link className={styles.closingDateButton} onClick={() => openClosingDateModal()}>
      Close Account
    </Link>
  )

  return (
    <section>
      <div className={styles.localHeader}>
        <div className={styles.localHeaderInner}>
          <Link to={'/closings'} className={styles.beneName}>
            Back to closings
          </Link>
        </div>
      </div>
      <div className={styles.contentContainer}>
        <h2 className={styles.sectionTitle}>Closing Management for {number}</h2>
        <div className={styles.row}>
          <section className={styles.tableWrapper}>
            {events && renderEventsTable()}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Next Actions</h2>
              <a className={styles.buttonLocal} onClick={() => openClosingEventModalCreate('')}>
                Add Custom
              </a>
              {nextActions && renderNextActions()}
            </section>
          </section>
          <section className={styles.oneThird}>
            <div className={styles.actions}>
              {setGoToAccountButton}
              {setClosingDateButton}
              {setClosingFeeButton}
              {retainFundsButton}
              {finalTaxFeeButton}
            </div>
            <DateOfDeathRef dateOfDeath={dateOfDeath} openDateOfDeathModal={openDateOfDeathModal} />
            <AnnualFeeRef daysSinceLastAnnualFee={daysSinceLastAnnualFee} />
            <ClosingFeeRef closing_fee_data={closing_fee_data} />
            <Notes accountNumber={number} dispatch={dispatch} notes={notes} type="closing" />
          </section>
        </div>
      </div>
      {showRetainFundsDialog && (
        <Dialog
          cancelButtonText="No"
          confirmButtonText="Yes"
          headerText="Sweep remaining balance into Retained Funds for Closing?"
          onCancel={() => setShowRetainFundsDialog(false)}
          onConfirm={handleRetainFunds}
          isSubmitting={isSubmittingRetainFunds}
        ></Dialog>
      )}
      {showFinalTaxFeeDialog && (
        <Dialog
          cancelButtonText="No"
          confirmButtonText="Yes"
          headerText="Apply Final Tax Fee?"
          onCancel={() => setShowFinalTaxFeeDialog(false)}
          onConfirm={handleFinalTaxFee}
          isSubmitting={isSubmittingFinalTaxFee}
        ></Dialog>
      )}
    </section>
  )
}
